import { FormValues } from '@wix/form-viewer';
import { FormContext } from '../../../../utils/context/contextFactory';
import { ActionFactoryParams } from '../../../../utils/ControlledComponent/ControlledComponent.types';
import {
  FormState,
  ServiceData,
  SlotService,
} from '../../../../utils/state/types';
import { BOOKINGS_APP_DEF_ID } from '../../../../utils/flow-api-adapter/consts';
import { ErrorHandlers } from '../errorHandlers/errorHandlers';
import { isValidEmail } from '../../../../utils/form-validations';
import { LineItem } from '@wix/ambassador-totals-calculator/types';
import { FormStatus } from '../../../../types/form-state';
import { PriceInfo } from '@wix/ambassador-bookings-v2-price-info/types';
import {
  mapPriceInfoToPaymentDetails,
  mapVariantsToLineItems,
} from '../../../../utils/mappers/dynamic-price.mapper';
import { FormApi } from '../../../../api/FormApi';
import { MoneyDto, SelectedVariants } from '@wix/bookings-uou-types';
import { getServiceSlotIdentifier, mapToArray } from '../../../../utils';
import { generateCouponScope } from '../../../../consts/coupon';
import { getPaymentOption } from '../../../../api/implementations/getPaymentOption/getPaymentOption';
import Experiments from '@wix/wix-experiments';
import { WixFormFields } from '../../../../types/wix-forms';
import { groupSelectedAddonsByGroupId } from '../../../../utils/payment/modifiers/groupSelectedAddonsByGroupId';
import { ExperimentsConsts } from '../../../../consts/experiments';

export type CalculatePaymentDetails = ({
  wixFormValues,
}: {
  wixFormValues?: FormValues;
}) => Promise<void>;

export function createCalculatePaymentDetailsAction({
  actionFactoryParams,
  errorHandlers,
}: {
  actionFactoryParams: ActionFactoryParams<FormState, FormContext>;
  errorHandlers: ErrorHandlers;
}): CalculatePaymentDetails {
  return async ({ wixFormValues }) => {
    const [state, setState] = actionFactoryParams.getControllerState();
    const { formApi, reportError, experiments } = actionFactoryParams.context;
    const { selectedNumberOfParticipants, businessInfo } = state;
    const email = wixFormValues?.[WixFormFields.EMAIL] as string;

    setState({
      status: FormStatus.PROCESSING_PAYMENT_DETAILS,
    });

    try {
      const priceInfoList: {
        [key: string]: PriceInfo;
      } = {};
      let updatedServiceData: ServiceData = { ...state.serviceData };

      await Promise.all(
        mapToArray<SlotService>(state.serviceData.slotServices).map(
          async (slotService) => {
            const { nestedSlot, dynamicPriceInfo } = slotService;
            const identifer = getServiceSlotIdentifier(slotService.nestedSlot);
            const priceInfo = await previewPrice({
              serviceId: nestedSlot.serviceId,
              resourceId: nestedSlot.resource?.id!,
              selectedVariants: dynamicPriceInfo?.selectedVariants,
              formApi,
              experiments,
            });

            if (priceInfo?.bookingLineItems?.length) {
              const dynamicPriceOptions =
                dynamicPriceInfo?.serviceOptionsAndVariants?.options!;
              const locale = businessInfo.dateRegionalSettingsLocale!;
              const paymentDetails = mapPriceInfoToPaymentDetails({
                priceInfo,
                dynamicPriceOptions,
                locale,
                experiments,
              });

              priceInfoList[identifer] = priceInfo;

              updatedServiceData = {
                ...state.serviceData,
                slotServices: {
                  ...state.serviceData.slotServices,
                  [identifer]: {
                    ...state.serviceData.slotServices[identifer],
                    dynamicPriceInfo: {
                      ...state.serviceData.slotServices[identifer]
                        .dynamicPriceInfo,
                      paymentDetails,
                    },
                  },
                },
              };
            }
          },
        ),
      );

      setState({ serviceData: updatedServiceData });

      const lineItems: LineItem[] = [];
      const lineItemToIdentifierMap: { [key: string]: string } = {};

      mapToArray<SlotService>(state.serviceData.slotServices).forEach(
        (slotService) => {
          const {
            service,
            paymentDetails,
            dynamicPriceInfo,
            selectedPaymentType,
            nestedSlot,
            selectedAddons,
            memberships,
            selectedPaymentOption,
          } = slotService;
          const { addonsPaymentOptions } = service.payment.paymentDetails;
          const identifier = getServiceSlotIdentifier(slotService.nestedSlot);
          const priceInfo = priceInfoList[identifier];
          let price: number;
          let deposit: number | undefined;
          const isDynamicPrice = !!priceInfo?.calculatedPrice;

          if (priceInfo?.bookingLineItems?.length) {
            price =
              Number(priceInfo?.calculatedPrice) ||
              Number(dynamicPriceInfo?.defaultPrice?.amount!);
            deposit = priceInfo?.deposit!;
          } else {
            price = service.payment.paymentDetails.price;
            deposit = paymentDetails.minCharge;
          }
          const selectedMembership = memberships?.eligibleMemberships?.find(
            (membership) => membership?.id === selectedPaymentOption.id,
          );

          const paymentOption = getPaymentOption(
            selectedPaymentType,
            deposit,
            selectedMembership,
            addonsPaymentOptions,
            experiments.enabled(ExperimentsConsts.AddonsWithPlanOnlyUOU),
          );

          lineItemToIdentifierMap[nestedSlot.lineItemId] = identifier;

          lineItems.push({
            id: nestedSlot.lineItemId,
            catalogReference: {
              catalogItemId: service.id,
              appId: BOOKINGS_APP_DEF_ID,
            },
            price: selectedMembership
              ? '0'
              : String(
                price * (isDynamicPrice ? 1 : selectedNumberOfParticipants),
              ),
            ...(deposit && !selectedMembership
              ? {
                depositAmount: String(
                  deposit *
                  (isDynamicPrice ? 1 : selectedNumberOfParticipants),
                ),
              }
              : {}),
            quantity: 1,
            couponScopes: [generateCouponScope(service.id)],
            paymentOption,
            ...(selectedAddons && Object.keys(selectedAddons).length > 0
              ? {
                modifierGroups: Object.entries(
                  groupSelectedAddonsByGroupId(selectedAddons),
                ).map(([groupId, addons]) => ({
                  id: groupId,
                  modifiers: addons.map(({ addon }) => ({
                    id: addon.id,
                    quantity: addon.quantity,
                    price: addon.price || '0',
                  })),
                })),
              }
              : {}),
          });
        },
      );

      const { priceSummary, payNow, payLater, calculatedLineItems, currency } =
        await formApi.calculateTotalPrice({
          lineItems,
          email: isValidEmail(email) ? email : undefined,
          onError: (error) => {
            errorHandlers.addError(error);
          },
        });

      const updatedSlotServices = { ...state.serviceData.slotServices };
      calculatedLineItems?.forEach((calculatedItem) => {
        const lineItemId = calculatedItem.lineItemId!;
        const identifier = lineItemToIdentifierMap[lineItemId];
        const slotService = state.serviceData.slotServices[identifier];

        if (slotService) {
          updatedSlotServices[identifier] = {
            ...updatedSlotServices[identifier],
            paymentDetails: {
              ...updatedSlotServices[identifier].paymentDetails,
              minPrice: {
                ...slotService.paymentDetails.minPrice,
                price: Number(
                  calculatedItem.pricesBreakdown?.lineItemPrice?.amount,
                ),
              } as MoneyDto,
              price: Number(
                calculatedItem.pricesBreakdown?.lineItemPrice?.amount,
              ),
              currency: currency || 'USD',
            },
          };
        }
      });

      setState({
        serviceData: {
          ...state.serviceData,
          slotServices: updatedSlotServices,
          summaryPaymentDetails: {
            subtotalPerParticipant:
              Number(priceSummary?.subtotal?.amount) /
              selectedNumberOfParticipants,
            tax: Number(priceSummary?.tax?.amount),
            totalPrice: Number(priceSummary?.total?.amount),
            payNow: Number(payNow?.total?.amount),
            payLater: payLater?.total?.amount
              ? Number(payLater?.total?.amount)
              : state.serviceData.summaryPaymentDetails.payLater,
          },
        },
        status: FormStatus.IDLE,
      });
    } catch (error) {
      errorHandlers.addError(error as any);
      setState({ status: FormStatus.IDLE });
      reportError(error as any);
    }
  };
}

async function previewPrice({
  formApi,
  serviceId,
  resourceId,
  selectedVariants,
  experiments,
}: {
  formApi: FormApi;
  serviceId: string;
  resourceId: string;
  selectedVariants?: SelectedVariants[];
  experiments: Experiments;
}): Promise<PriceInfo> {
  if (selectedVariants) {
    const bookingLineItems = mapVariantsToLineItems({
      serviceId,
      resourceId,
      selectedVariants,
      experiments,
    });
    if (bookingLineItems.length) {
      const previewPriceResponse = await formApi.previewPrice({
        bookingLineItems,
      });
      return previewPriceResponse?.priceInfo!;
    }
  }
  return {};
}
